import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Search, Plus, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { getUserTypeDisplayName, getUserTypeBadgeVariant } from '@/utils/userTypeDisplay';
import { getSupportRolesForSport, formatRoleName } from '@/data/sportRoles';

interface User {
  id: string;
  username?: string;
  first_name?: string;
  last_name?: string;
  role?: string;
  user_type: 'supabase' | 'internal';
}

interface SupportStaffConnection {
  id: string;
  user_id: string;
  support_role?: string;
  user?: User;
}

interface AddSupportStaffFormProps {
  isOpen: boolean;
  onClose: () => void;
  teamId: number;
  teamName: string;
  teamSport?: string;
  onUpdate: () => void;
}


export const AddSupportStaffForm = ({
  isOpen,
  onClose,
  teamId,
  teamName,
  teamSport = 'Basketball',
  onUpdate
}: AddSupportStaffFormProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [selectedRole, setSelectedRole] = useState('');
  const [loading, setLoading] = useState(false);
  const [supportStaff, setSupportStaff] = useState<SupportStaffConnection[]>([]);

  // Load current team support staff
  const loadTeamSupportStaff = async () => {
    try {
      setLoading(true);
      
      const { data: teamSupport, error: supportError } = await supabase
        .from('team_support_staff')
        .select('*')
        .eq('team_id', teamId);

      if (!supportError && teamSupport) {
        const supportWithUsers = await Promise.all(
          teamSupport.map(async (support) => {
            let userData = null;
            
            // Try Supabase users first
            const { data: profile } = await supabase
              .from('profiles')
              .select('id, username, first_name, last_name, role')
              .eq('id', support.staff_id)
              .single();
            
            if (profile) {
              userData = { ...profile, user_type: 'supabase' as const };
            } else {
              // Try internal users
              const { data: internalProfile } = await supabase
                .from('internal_user_profiles')
                .select('user_id, username, first_name, last_name, role')
                .eq('user_id', support.staff_id)
                .single();
              
              if (internalProfile) {
                userData = { 
                  id: internalProfile.user_id, 
                  username: internalProfile.username,
                  first_name: internalProfile.first_name,
                  last_name: internalProfile.last_name,
                  role: internalProfile.role,
                  user_type: 'internal' as const 
                };
              }
            }
            
            return {
              id: support.staff_id + '-support',
              user_id: support.staff_id,
              support_role: support.role,
              user: userData
            };
          })
        );
        setSupportStaff(supportWithUsers);
      }
    } catch (error) {
      console.error('Error loading team support staff:', error);
      toast.error('Failed to load team support staff');
    } finally {
      setLoading(false);
    }
  };

  // Search users
  const searchUsers = async () => {
    if (!searchTerm.trim()) {
      setAvailableUsers([]);
      return;
    }

    try {
      setLoading(true);
      
      // Search Supabase users
      const { data: supabaseUsers, error: supabaseError } = await supabase
        .from('profiles')
        .select('id, username, first_name, last_name, role')
        .or(`username.ilike.%${searchTerm}%,first_name.ilike.%${searchTerm}%,last_name.ilike.%${searchTerm}%`)
        .limit(10);

      // Search internal users
      const { data: internalUsers, error: internalError } = await supabase
        .from('internal_user_profiles')
        .select('user_id, username, first_name, last_name, role')
        .or(`username.ilike.%${searchTerm}%,first_name.ilike.%${searchTerm}%,last_name.ilike.%${searchTerm}%`)
        .limit(10);

      // Combine results
      const combined: User[] = [
        ...(supabaseUsers || []).map(user => ({
          id: user.id,
          username: user.username,
          first_name: user.first_name,
          last_name: user.last_name,
          role: user.role,
          user_type: 'supabase' as const
        })),
        ...(internalUsers || []).map(user => ({
          id: user.user_id,
          username: user.username,
          first_name: user.first_name,
          last_name: user.last_name,
          role: user.role,
          user_type: 'internal' as const
        }))
      ];

      // Filter out existing support staff
      const existingSupportIds = supportStaff.map(s => s.user_id);
      const filtered = combined.filter(user => !existingSupportIds.includes(user.id));

      setAvailableUsers(filtered);
    } catch (error) {
      console.error('Error searching users:', error);
      toast.error('Failed to search users');
    } finally {
      setLoading(false);
    }
  };

  // Add support staff
  const addSupportStaff = async (userId: string, userType: 'supabase' | 'internal') => {
    try {
      // Check if staff is already on the team
      const existingStaff = supportStaff.find(s => s.user_id === userId);
      if (existingStaff) {
        toast.error('This person is already on the support staff');
        return;
      }

      // Get current user ID for added_by field
      const { data: { user } } = await supabase.auth.getUser();
      let currentUserId = user?.id;
      
      // Try internal user if no Supabase user
      if (!currentUserId) {
        try {
          // Try session storage first
          const sessionData = localStorage.getItem('internal_user_session');
          if (sessionData) {
            const session = JSON.parse(sessionData);
            currentUserId = session.user?.id;
            console.log('Found internal user ID in session:', currentUserId);
          }
          
          // Fallback to direct localStorage
          if (!currentUserId) {
            const internalUserData = localStorage.getItem('internal_user');
            if (internalUserData) {
              const userData = JSON.parse(internalUserData);
              currentUserId = userData.id;
              console.log('Found internal user ID in localStorage:', currentUserId);
            }
          }
        } catch (e) {
          console.error('Error parsing internal user data:', e);
        }
        
        // Final fallback to localStorage key
        if (!currentUserId) {
          currentUserId = localStorage.getItem('internal_user_id');
          console.log('Using fallback internal user ID:', currentUserId);
        }
      }
      
      console.log('Current user ID for added_by:', currentUserId);
      
      if (!currentUserId) {
        console.error('Authentication failed - no user ID found in any storage method');
        toast.error('Authentication error: Unable to identify current user. Please try logging out and back in.');
        return;
      }

      // Validate that a role is selected
      if (!selectedRole?.trim()) {
        toast.error('Please select a role for the support staff member');
        return;
      }

      const { error } = await supabase
        .from('team_support_staff')
        .insert({
          team_id: teamId,
          staff_id: userId,
          role: selectedRole,
          added_by: currentUserId
        });
      
      if (error) {
        console.error('Database error:', error);
        if (error.code === '23505') {
          toast.error('This person is already on the support staff');
        } else {
          toast.error(`Failed to add support staff: ${error.message}`);
        }
        return;
      }

      toast.success('Support staff added successfully');
      loadTeamSupportStaff();
      onUpdate();
      setSearchTerm('');
      setSelectedRole('');
      setAvailableUsers([]);
    } catch (error) {
      console.error('Error adding support staff:', error);
      toast.error('Failed to add support staff');
    }
  };

  // Remove support staff
  const removeSupportStaff = async (userId: string) => {
    try {
      const { error } = await supabase
        .from('team_support_staff')
        .delete()
        .eq('team_id', teamId)
        .eq('staff_id', userId);
      
      if (error) {
        console.error('Database error:', error);
        toast.error(`Failed to remove support staff: ${error.message}`);
        return;
      }

      toast.success('Support staff removed successfully');
      loadTeamSupportStaff();
      onUpdate();
    } catch (error) {
      console.error('Error removing support staff:', error);
      toast.error('Failed to remove support staff');
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadTeamSupportStaff();
    }
  }, [isOpen, teamId]);

  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      searchUsers();
    }, 300);

    return () => clearTimeout(delayedSearch);
  }, [searchTerm]);

  const getUserDisplayName = (user?: User) => {
    if (!user) return 'Unknown User';
    return user.username || `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Unknown User';
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-card border-border text-foreground max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-foreground">
            Manage Support Staff - {teamName} {teamSport && `(${teamSport})`}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Add Support Staff Section */}
          <Card>
            <CardHeader>
              <CardTitle>Add New Support Staff</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="support-search">Search Support Staff</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="support-search"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Search by username or name..."
                      className="pl-10"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="support-role">Support Role</Label>
                  <Select value={selectedRole} onValueChange={setSelectedRole}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select role (required)" />
                    </SelectTrigger>
                    <SelectContent>
                      {getSupportRolesForSport(teamSport).map((role) => (
                        <SelectItem key={role} value={role}>
                          {formatRoleName(role)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Search Results */}
              {searchTerm && (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {availableUsers.map((user) => {
                    const isAlreadyStaff = supportStaff.some(s => s.user_id === user.id);
                    return (
                      <div key={user.id} className="flex items-center justify-between p-2 bg-muted rounded-lg">
                        <div>
                          <span className="font-medium">{getUserDisplayName(user)}</span>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="secondary" className="text-xs">
                              {user.role}
                            </Badge>
                            <Badge variant={getUserTypeBadgeVariant(user.user_type)} className="text-xs">
                              {getUserTypeDisplayName(user.user_type)}
                            </Badge>
                          </div>
                          {isAlreadyStaff && (
                            <span className="text-xs text-yellow-600 ml-2">(Already on staff)</span>
                          )}
                        </div>
                        <Button
                          onClick={() => addSupportStaff(user.id, user.user_type)}
                          size="sm"
                          className="bg-sports-green text-black hover:bg-sports-green/90 font-bold"
                          disabled={isAlreadyStaff || !selectedRole?.trim()}
                        >
                          <Plus className="h-3 w-3 mr-1" />
                          {isAlreadyStaff ? 'Added' : 'Add'}
                        </Button>
                      </div>
                    );
                  })}
                  {availableUsers.length === 0 && searchTerm && (
                    <p className="text-muted-foreground text-center py-4">No users found</p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Current Support Staff Section */}
          <Card>
            <CardHeader>
              <CardTitle>Current Support Staff ({supportStaff.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {supportStaff.map((staff) => (
                  <div key={staff.id} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                    <div>
                      <span className="font-medium">{getUserDisplayName(staff.user)}</span>
                      <div className="flex items-center gap-2 mt-1">
                        {staff.support_role && (
                          <Badge variant="outline" className="text-xs">
                            {formatRoleName(staff.support_role)}
                          </Badge>
                        )}
                        {staff.user && (
                          <Badge variant={getUserTypeBadgeVariant(staff.user.user_type)} className="text-xs">
                            {getUserTypeDisplayName(staff.user.user_type)}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <Button
                      onClick={() => removeSupportStaff(staff.user_id)}
                      size="sm"
                      variant="destructive"
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
                {supportStaff.length === 0 && (
                  <p className="text-muted-foreground text-center py-8">No support staff added yet</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
};